import { renderFrequencyOptions } from '@doctor/Encounter/Manage Orders/Modals/AddMedication'
import { ColumnDef } from '@tanstack/react-table'
import { PatientMedicationDataMock, PatientMedicationIncluded } from '@type/medication.type'
import { Badge, Table2 } from '@ui'
import { getIncludedData, parseIsoToLocalDate } from '@util'
import moment from 'moment'
import { useMemo } from 'react'
import { patientHistoryTableClassName } from '../Results/tableStyles'

type Props = {
  included?: PatientMedicationIncluded[]
  isLoading?: boolean
  records: PatientMedicationDataMock[]
}

const formatDate = (date?: string) =>
  date ? moment(date.includes('T') ? parseIsoToLocalDate(date) : date).format('DD/MM/YYYY') : '-'

export default function MedicationsTable({ included = [], isLoading, records }: Props) {
  const columns = useMemo<ColumnDef<PatientMedicationDataMock>[]>(
    () => [
      {
        id: 'medication',
        header: 'Medication',
        cell: ({ row }) => {
          const datum = row.original
          const product = getIncludedData(included, 'product', datum.relationships.product?.data?.id)?.attributes
          const drugName = product?.generic_name || product?.name || datum.attributes.nf_drug_name
          const label = [datum.attributes.form, drugName, datum.attributes.strength].filter(Boolean).join(' ')
          const frequency =
            getIncludedData(included, 'medication_frequency', datum.relationships.medication_frequency?.data?.id)
              ?.attributes?.code || renderFrequencyOptions(datum.attributes.custom_frequency)

          return (
            <div className="grid gap-1">
              <span>{label || '-'}</span>
              <span>{`Frequency: ${frequency || '-'}`}</span>
            </div>
          )
        }
      },
      {
        accessorKey: 'attributes.start_at',
        header: 'Start Date',
        cell: ({ row }) => <span>{formatDate(row.original.attributes.start_at)}</span>
      },
      {
        accessorKey: 'attributes.end_at',
        header: 'End Date',
        cell: ({ row }) => <span>{formatDate(row.original.attributes.end_at)}</span>
      },
      {
        accessorKey: 'meta.status',
        header: 'Status',
        cell: ({ row }) => (
          <Badge
            label={row.original.meta?.status === 'onhold' ? 'onHold' : row.original.meta?.status}
            size="small"
            status={row.original.meta?.status}
          />
        )
      }
    ],
    [included]
  )

  return (
    <Table2
      className={patientHistoryTableClassName}
      columns={columns}
      data={records}
      emptyDescription="No medications found."
      isLoading={isLoading}
      message="No medications found"
      showPagination={false}
      wrapperClass="overflow-x-auto"
    />
  )
}
